import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { AbstractFormComponent } from '@app/shared/common/abstract.component';
import { KycService } from '@app/core/kyc.service';
import { routerTransition } from '@app/router.animations';
import { QuestionBase } from '@app/core/models/question/question-base';
import { QuestionService } from '@app/core/question.service';
import { QuestionControlService } from '@app/core/question-control.service';
import { Router, ActivatedRoute } from '@angular/router';
import { NavTabService } from '@app/core/navTab.service';
import * as moment from 'moment';
import { getAgeFromDOB } from '@app/features/my-needs/my-needs';
import * as utils from '@app/core/utils';

declare var $;
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  animations: [routerTransition()],
  host: { '[@routerTransition]': '' }
})

export class ProfileComponent extends AbstractFormComponent implements OnInit, OnDestroy {
  questionnaire: QuestionBase<any>[];
  messageEmailInvalid: string;
  messageRequired: string;
  numberBoys: number;
  numberGirls: number;
  userProfile: any;
  dateString: string;
  isErrorDate: boolean;
  firstInvalidControlName = '';
  @ViewChild('email', { read: ElementRef }) email: ElementRef;
  @ViewChild('gender', { read: ElementRef }) gender: ElementRef;
  @ViewChild('dob', { read: ElementRef }) dob: ElementRef;
  @ViewChild('marital', { read: ElementRef }) marital: ElementRef;
  @ViewChild('kids', { read: ElementRef }) kids: ElementRef;

  constructor(private kycService: KycService,
    private questionService: QuestionService,
    private qcs: QuestionControlService,
    private router: Router,
    private navTabService: NavTabService) {
    super();
    this.questionnaire = questionService.buildProfileQuestionnaire(this.kycService.kyc.profile.getQuestion());
  }

  ngOnInit(): void {
    this.form = this.qcs.toFormGroup(this.questionnaire);
    this.userProfile = this.kycService.kyc.profile.userProfile;
    this.dateString = this.kycService.kyc.profile.dob;

    if (this.userProfile) {
      const keys = Object.keys(this.userProfile);
      keys.forEach(key => {
        this.form.controls[key].setValue(this.userProfile[key]);
      });
    }

    this.numberBoys = this.kycService.kyc.profile.quantityBoys;
    this.numberGirls = this.kycService.kyc.profile.quantityGirls;
  }

  getDateChanged(event) {
    const formatDate = moment([Number(event.year), Number(event.month) - 1, Number(event.day)]).toString();
    this.kycService.kyc.profile.dob = formatDate;
    this.form.controls['age'].setValue(getAgeFromDOB(formatDate));
    this.form.updateValueAndValidity();
    this.isErrorDate = this.onErrorDate(this.kycService.kyc.profile.dob)
  }
  onErrorDate(dob) {
    const todayDate = new Date(moment(new Date()).format('L'));
    const todayDOB = new Date(moment(dob).format('L'));
    const dobTime = todayDOB.getTime();
    const todayTime = todayDate.getTime()
    return todayTime < dobTime
  }
  onKidChanged(event) {
    let quantityBoys = 0, quantityGirls = 0;
    if (event.kidBoys || event.kidGirls) {
      for (let index = 0; index < event.kidBoys.length; index++) {
        if (event.kidBoys[index].checked) {
          quantityBoys++;
        }
        if (event.kidGirls[index].checked) {
          quantityGirls++;
        }
      }
    }

    this.kycService.kyc.profile.quantityBoys = quantityBoys;
    this.kycService.kyc.profile.quantityGirls = quantityGirls;
    this.form.controls['kids'].setValue(quantityBoys + quantityGirls);
    this.form.updateValueAndValidity();
  }

  onClickNext() {
    utils.markFormGroupTouched(this.form);
    if (this.form.valid && !this.isErrorDate) {
      if (!this.kycService.kyc.profile.dob) {
        const date = moment().add(-38, 'year').toString();
        this.kycService.kyc.profile.dob = date;
        this.form.controls['age'].setValue(getAgeFromDOB(date));
      }

      this.kycService.kyc.profile.completed = true;
      this.kycService.kyc.profile.userProfile = this.form.value;
      this.navTabService.setActiveTab('coverage');
      this.router.navigateByUrl('/journey/coverage');
    } else {
      this.kycService.kyc.profile.completed = false;
      this.firstInvalidControlName = utils.findFirstInvalidControlName(this.form);
      this.scrollTo(this.firstInvalidControlName);
    }
  }

  scrollTo(key: string) {
    const currentScrollPos = document.getElementById('custom-scrollbar');
    switch (key) {
      case 'email':
        this.scrollToElement(currentScrollPos, this.email.nativeElement);
        break;
      case 'age':
        this.scrollToElement(currentScrollPos, this.dob.nativeElement);
        break;
      default:
        break;
    }
  }

  ngOnDestroy() {
    utils.markFormGroupTouched(this.form);
    if (this.form.valid) {
      this.kycService.kyc.profile.completed = true;
      this.kycService.kyc.profile.userProfile = this.form.value;
    } else {
      this.kycService.kyc.profile.completed = false;
    }
  }

  onEmailValid(valid: boolean) {
    this.kycService.kyc.profile.emailInvalid = valid;
    this.kycService.kyc.profile.userProfile = this.form.value;
  }

  onSelected(key: string, selected: boolean) {
    const currentScrollPos = document.getElementById('custom-scrollbar');
    switch (key) {
      case 'gender':
        this.scrollToElement(currentScrollPos, this.dob.nativeElement);
        break;
      case 'marital':
        this.scrollToElement(currentScrollPos, this.kids.nativeElement);
        break;
      default:
        break;
    }
  }

  handleDOBCompleted() {
    const currentScrollPos = document.getElementById('custom-scrollbar');
    this.scrollToElement(currentScrollPos, this.marital.nativeElement);
  }

  scrollToElement(parent, element) {
    parent.scrollTo({ top: $(element).position().top, behavior: 'smooth' });
  }

  handleEmailClicked(type) {
    const isAndroidDevice = /android/i.test(navigator.userAgent.toLowerCase());
    if (isAndroidDevice) {
      const currentScrollPos = document.getElementById('custom-scrollbar');
      if (type === 'focusin') {
        currentScrollPos.scrollTo({
          top: 150,
          behavior: 'smooth'
        });
      } else {
        this.scrollToElement(currentScrollPos, this.email.nativeElement);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/features/profile/profile.component.ts